import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system/legacy';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { GlCodeMappingModal } from '@/components/accounting/gl-code-mapping-modal';
import { QbBundleProgressView } from '@/components/accounting/qb-bundle-progress';
import { QbResetToggle, RESET_ACCOUNTS_WARNING } from '@/components/accounting/qb-reset-toggle';
import { UnknownVendorModal } from '@/components/accounting/unknown-vendor-modal';
import { UnresolvedOwnersModal } from '@/components/accounting/unresolved-owners-modal';
import { SecureUnlockModal } from '@/components/secure/secure-unlock-modal';
import { Text } from '@/components/ui';
import { Modal } from '@/components/ui/modal';
import { useAccountingPermissions, useAccounts, useInvalidateAccounting, useVendors } from '@/hooks/use-accounting';
import { useAssociation } from '@/hooks/use-data';
import { accountingService } from '@/services/accounting-service';
import { apiService } from '@/services/api-service';
import {
	applyOwnerResolutions,
	applyVendorResolutions,
	buildQbCompileResolutions,
	compileUploadAndStart,
	type OwnerResolution,
	probeUnresolvedOwners,
	probeUnresolvedVendors,
	type QbCompileClientResolutions,
	type VendorResolution,
} from '@/services/qb-compile-client';
import { type IifListsSummary, importIifLists } from '@/services/qb-lists-import';
import { useSecureUnlock } from '@/services/secure-unlock-context';
import { ImportBatchKind, type JournalOnlyEntry } from '@/types/models';
import { Alert } from '@/utils/alert';
import { type StandardGl } from '@/utils/gl-standard-match';
import { type IIFParse, parseIIF } from '@/utils/iif-parser';
import { type ParsedEntry, parseCsv, parseJournalRows } from '@/utils/journal-csv-parser';
import { journalCompleted, sumJournalCounts, type QbBundleProgressStatus } from '@/utils/qb-bundle-progress';
import {
	applyGlCodeResolutions,
	type GlCodeMapping,
	type GlCodeResolution,
	probeGlCodeMappings,
	type QbCompileDetails,
	type QbDetailRow,
	type UnresolvedOwner,
	type UnresolvedVendor,
} from '@/utils/qb-compile';
import { classifyQbFile, type QbFileType } from '@/utils/qb-file-classify';
import { buildQbPreflightReport, type QbPreflightFinding, type QbPreflightReport } from '@/utils/qb-preflight';
import { parseTransactionsCsv } from '@/utils/transactions-csv-parser';
import { parseVendorCsv } from '@/utils/vendor-csv-parser';

interface Props {
	associationId: string;
	onClose: () => void;
	/** Re-open the progress UI for an existing workflow-driven batch (no re-upload). */
	monitorBatchId?: string;
	/** Batch createdAt — used for elapsed-time display when monitoring. */
	monitorCreatedAt?: string;
}

type Phase = 'pick' | 'review' | 'starting' | 'running' | 'done' | 'error' | 'failed';

interface PickedFile {
	name: string;
	text: string;
	type: QbFileType;
	reason: string;
}

// Only these three flow through the Workflow. The IIF is applied CLIENT-SIDE
// first (lists import: GL codes, customers, vendors with tax IDs encrypted in
// the browser — Secure Fields pattern) before the transactions compile.
const WORKFLOW_TYPES: QbFileType[] = ['journal_csv', 'transactions_csv', 'vendor_transactions_csv'];

// Once a batch is created the IIF lists (GL codes / customers / vendors) may have
// partially written before a later step threw. Per the bulk-import pattern, the
// failure must point the user at the Batches list rather than imply nothing saved.
const withPartialSaveHint = (message: string, createdBatchId: string | null): string =>
	createdBatchId
		? `${message}\n\nSome data may have partially saved — refresh the Batches list (batch ${createdBatchId.slice(0, 8)}) before retrying.`
		: message;

const TYPE_LABEL: Record<QbFileType, string> = {
	iif: 'IIF Lists (chart, customers, vendors)',
	journal_csv: 'Journal',
	transactions_csv: 'Transactions (AR)',
	vendor_transactions_csv: 'Vendor (AP)',
	unknown: 'Unknown',
};

// Override choices are the three CSV workflow types; IIF detection is exact.
const TYPE_CHOICES: QbFileType[] = ['journal_csv', 'transactions_csv', 'vendor_transactions_csv'];

// Blocking findings that auto-correction resolves (synthesized entries cover
// the missing records, so AR/AP tie out despite the export issues).
const CORRECTABLE_FINDINGS = new Set(['coverage_gap', 'ar_mismatch', 'ap_mismatch', 'iif_balance_mismatch']);

interface BundleStatus extends QbBundleProgressStatus {
	workflowStatus: string | null;
	workflowError?: string | null;
	journal: Record<string, number>;
	enrichment: { ar?: Record<string, number>; ap?: Record<string, number> };
	done: boolean;
	hasErrors?: boolean;
	filesErrored?: number;
}

const usd = (cents: number): string => {
	const sign = cents < 0 ? '-' : '';
	return `${sign}$${(Math.abs(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** 'YYYY-MM' → 'Mon YYYY' for the stats header. */
const monthRange = (key: string): string => {
	const [y, m] = key.split('-').map(Number);
	return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

function makeSessionId(): string {
	const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
	if (c?.randomUUID) return c.randomUUID();
	return `s-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

const fmtBytes = (n: number | null | undefined): string => {
	if (n == null || !Number.isFinite(n)) return '';
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
	return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Per-file download list (issue #248). Web-only — the raw files are pulled
 * through the authenticated API and saved via the browser. Each row shows the
 * file's type + per-file processing status so you can verify exactly what was
 * uploaded before re-using it on a dev system.
 */
const BundleFileDownloads: React.FC<{
	associationId: string;
	importBatchId: string;
	files: NonNullable<QbBundleProgressStatus['files']>;
}> = ({ associationId, importBatchId, files }) => {
	const [downloading, setDownloading] = useState<string | null>(null);
	const [err, setErr] = useState<string | null>(null);
	// Collapsed by default (issue #248 follow-up): a year-split compiled rebuild
	// produces 30+ NDJSON parts, and a wall of Download buttons buried the
	// "Rebuild complete" summary. The full list still lives on the Batch Details
	// page; here it's one tap away without dominating the modal.
	const [expanded, setExpanded] = useState(false);
	const downloadable = files.filter((f) => !!f.r2Key);
	if (Platform.OS !== 'web' || downloadable.length === 0) return null;
	const download = async (f: NonNullable<QbBundleProgressStatus['files']>[number]): Promise<void> => {
		if (!f.r2Key) return;
		setErr(null);
		setDownloading(f.r2Key);
		try {
			await accountingService.downloadImportBatchFile({
				importBatchId, associationId, r2Key: f.r2Key, filename: f.filename ?? f.r2Key.split('/').pop() ?? 'download',
			});
		} catch (e) {
			setErr((e as Error)?.message ?? 'Download failed');
		} finally {
			setDownloading(null);
		}
	};
	return (
		<View style={styles.dlBox}>
			<Pressable style={styles.disclosureHeader} onPress={() => setExpanded((v) => !v)}>
				<Ionicons name={expanded ? 'chevron-down' : 'chevron-forward'} size={16} color="#607d8b" />
				<Text style={styles.statsTitle}>RAW FILES ({downloadable.length})</Text>
			</Pressable>
			{expanded && downloadable.map((f) => (
				<View key={f.r2Key} style={styles.dlRow}>
					<View style={{ flex: 1, minWidth: 0 }}>
						<Text style={styles.dlName} numberOfLines={1}>{f.filename ?? f.r2Key}</Text>
						<Text style={styles.dlMeta}>
							{(f.overrideType ?? f.detectedType ?? 'file')} · {f.status ?? 'pending'}{f.byteSize ? ` · ${fmtBytes(f.byteSize)}` : ''}
						</Text>
					</View>
					<Pressable style={styles.dlBtn} onPress={() => download(f)} disabled={downloading === f.r2Key}>
						<Ionicons name="download-outline" size={16} color="#1976d2" />
						<Text style={styles.dlBtnText}>{downloading === f.r2Key ? '…' : 'Download'}</Text>
					</Pressable>
				</View>
			))}
			{err && <Text style={styles.error}>{err}</Text>}
		</View>
	);
};

/**
 * Journal-only entries (issue #248): the entries that posted to the ledger but
 * produced no customer/vendor row. The "Rebuild complete" warning tells you how
 * many there are; this lets you actually SEE them (collapsible) and download
 * just them as a CSV for review / re-import. Fetches lazily on mount and
 * renders nothing when the batch has none — so it's safe to drop on every done
 * screen, including monitor mode where the compile-time count isn't known.
 */
const JournalOnlyEntries: React.FC<{ associationId: string; importBatchId: string }> = ({ associationId, importBatchId }) => {
	const [rows, setRows] = useState<JournalOnlyEntry[] | null>(null);
	const [total, setTotal] = useState(0);
	const [truncated, setTruncated] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [err, setErr] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		accountingService.fetchJournalOnlyEntries({ associationId, importBatchId })
			.then((r) => { if (!cancelled) { setRows(r.rows); setTotal(r.total); setTruncated(r.truncated); } })
			.catch(() => { if (!cancelled) setRows([]); });
		return () => { cancelled = true; };
	}, [associationId, importBatchId]);

	if (!rows || rows.length === 0) return null;

	const download = async (): Promise<void> => {
		setErr(null);
		setDownloading(true);
		try {
			await accountingService.downloadJournalOnlyEntries({ associationId, importBatchId });
		} catch (e) {
			setErr((e as Error)?.message ?? 'Download failed');
		} finally {
			setDownloading(false);
		}
	};

	const dateStr = (ms: number): string => (ms ? new Date(ms).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '—');

	return (
		<View style={styles.joBox}>
			<View style={styles.joHeaderRow}>
				<Pressable style={styles.disclosureHeader} onPress={() => setExpanded((v) => !v)}>
					<Ionicons name={expanded ? 'chevron-down' : 'chevron-forward'} size={16} color="#a06b00" />
					<Text style={styles.joTitle}>JOURNAL-ONLY ENTRIES ({total})</Text>
				</Pressable>
				{Platform.OS === 'web' && (
					<Pressable style={styles.dlBtn} onPress={download} disabled={downloading}>
						<Ionicons name="download-outline" size={16} color="#1976d2" />
						<Text style={styles.dlBtnText}>{downloading ? '…' : 'Download CSV'}</Text>
					</Pressable>
				)}
			</View>
			{expanded && (
				<View style={styles.joList}>
					{rows.map((r) => (
						<View key={r.entryId} style={styles.joRow}>
							<View style={{ flex: 1, minWidth: 0 }}>
								<Text style={styles.joRowTop} numberOfLines={1}>
									{dateStr(r.postedOn)}{r.num ? ` · #${r.num}` : ''}{r.memo ? ` · ${r.memo}` : ''}
								</Text>
								<Text style={styles.joRowSub} numberOfLines={1}>
									{r.classification}{r.reason ? ` — ${r.reason}` : ''}
								</Text>
							</View>
							<Text style={styles.joAmount}>{usd(r.amountCents)}</Text>
						</View>
					))}
					{truncated && (
						<Text style={styles.joTruncated}>
							Showing the first {rows.length.toLocaleString()} of {total.toLocaleString()} — download the CSV for the complete list.
						</Text>
					)}
				</View>
			)}
			{err && <Text style={styles.error}>{err}</Text>}
		</View>
	);
};

function fileFlagsFromStatus(files: QbBundleProgressStatus['files'] | undefined): {
	journalFileCount: number;
	hasArFile: boolean;
	hasApFile: boolean;
} {
	const list = files ?? [];
	const typeOf = (f: NonNullable<QbBundleProgressStatus['files']>[number]) =>
		f.overrideType ?? f.detectedType ?? '';
	return {
		journalFileCount: Math.max(1, list.filter((f) => typeOf(f) === 'journal_csv').length),
		hasArFile: list.some((f) => typeOf(f) === 'transactions_csv'),
		hasApFile: list.some((f) => typeOf(f) === 'vendor_transactions_csv'),
	};
}

export const ImportQbBundleModal: React.FC<Props> = ({
	associationId,
	onClose,
	monitorBatchId,
	monitorCreatedAt,
}) => {
	const isMonitor = !!monitorBatchId;
	const { isSuperAdmin } = useAccountingPermissions();
	const { data: association } = useAssociation(associationId);
	// Existing customers (accounts) + vendors — used to badge each IIF list toggle
	// with a new-vs-edit count. Matched by the same keys the importers use:
	// vendors by name, customers by their source CUST.NAME (accounts.externalName).
	const { data: existingAccountsData } = useAccounts({ associationId, limit: 1000, includeArchived: true });
	const { data: existingVendorsData } = useVendors({ limit: 1000 });
	const [phase, setPhase] = useState<Phase>(isMonitor ? 'running' : 'pick');
	const [files, setFiles] = useState<PickedFile[]>([]);
	const [reset, setReset] = useState(false);
	// R2 key of the pre-reset backup taken when Reset was checked — surfaced on
	// the done screen so the admin can download the safety snapshot.
	const [resetBackupKey, setResetBackupKey] = useState<string | null>(null);
	const [preflight, setPreflight] = useState<QbPreflightReport | null>(null);
	const [overrideBlocked, setOverrideBlocked] = useState(false);
	// v2 compiled import (issue #212): the browser compiles the full insert
	// graph and the workflow bulk-loads it. Super-admin only; default ON.
	const [compiledMode, setCompiledMode] = useState(true);
	// Sticky once a compiled run launches, so the progress view doesn't flash
	// the legacy per-step (dots) view in the window between setPhase('running')
	// and the first /status poll populating the compiled_ndjson file rows.
	const [compiledRun, setCompiledRun] = useState(false);
	const [autoCorrect, setAutoCorrect] = useState(false);
	const [compileStage, setCompileStage] = useState<string | null>(null);
	// Parsed IIF lists (chart/customers/vendors) — applied client-side BEFORE
	// the transactions, in the SAME run. Tax IDs require secure-unlock.
	const [iifParsed, setIifParsed] = useState<IIFParse | null>(null);
	// Per-category include toggles for the IIF lists — lets the user import
	// customers only, skip vendors, etc. Default ON (whole-file import).
	const [includeChart, setIncludeChart] = useState(true);
	const [includeCustomers, setIncludeCustomers] = useState(true);
	const [includeVendors, setIncludeVendors] = useState(true);
	const [listsSummary, setListsSummary] = useState<IifListsSummary | null>(null);

	// IIF lists filtered by the include toggles — this is what actually gets
	// applied. Excluded categories are emptied so importIifLists is a no-op for them.
	const filteredIifParsed = useMemo<IIFParse | null>(() => {
		if (!iifParsed) return null;
		return {
			...iifParsed,
			accounts: includeChart ? iifParsed.accounts : [],
			customers: includeCustomers ? iifParsed.customers : [],
			vendors: includeVendors ? iifParsed.vendors : [],
		};
	}, [iifParsed, includeChart, includeCustomers, includeVendors]);

	// New-vs-edit counts per list. "edit" = the parsed row's identity key already
	// exists (vendors by name, customers by source CUST.NAME → accounts.externalName).
	const listCounts = useMemo(() => {
		const norm = (s?: string) => (s ?? '').trim().toLowerCase();
		const vendorNames = new Set((existingVendorsData?.data ?? []).map((v) => norm(v.name)));
		const customerNames = new Set((existingAccountsData?.data ?? []).map((a) => norm(a.externalName)));
		const tally = (rows: IIFParse['customers'], existing: Set<string>) => {
			let edit = 0;
			let add = 0;
			for (const r of rows) {
				const nm = norm(r.raw?.NAME);
				if (!nm) continue;
				if (existing.has(nm)) edit++;
				else add++;
			}
			return { add, edit, total: rows.length };
		};
		return {
			customers: iifParsed ? tally(iifParsed.customers, customerNames) : null,
			vendors: iifParsed ? tally(iifParsed.vendors, vendorNames) : null,
		};
	}, [iifParsed, existingVendorsData, existingAccountsData]);
	// Entries that compiled to the journal but produced no domain row
	// (journal-only General Journal entries + any projection that failed). These
	// land in projection_audit (reviewable on the import batch); the count is
	// surfaced on the done screen so a "complete" rebuild never silently hides
	// dropped projections.
	const [compiledAuditCount, setCompiledAuditCount] = useState(0);
	// GL codes the compile is creating (not matched to the standard chart) —
	// surfaced on the result so the user can confirm what got added.
	const [createdGlCodes, setCreatedGlCodes] = useState<Array<{ code: string; name: string }>>([]);
	// Unknown-payee resolution: the probe compile surfaces payees referenced by
	// checks/bills that aren't known vendors. We pause, prompt the user to
	// create-or-map each, then resume the real compile with the enriched maps.
	const [unknownVendors, setUnknownVendors] = useState<UnresolvedVendor[] | null>(null);
	// Owner (A/R) names that didn't resolve to an account — a BLOCKING review so
	// unmatched customer transactions can't be dropped silently (West Harbor).
	const [unresolvedOwners, setUnresolvedOwners] = useState<UnresolvedOwner[] | null>(null);
	// GL-code mapping preview: how each journal GL code maps onto the standard
	// chart. Shown before the compile so the user can review/override every code.
	const [glMappings, setGlMappings] = useState<GlCodeMapping[] | null>(null);
	const pendingCompileRef = useRef<{
		batchId: string;
		entries: ParsedEntry[];
		details: QbCompileDetails;
		resolutions: QbCompileClientResolutions;
		standardDefaults: StandardGl[];
	} | null>(null);
	const [unlockOpen, setUnlockOpen] = useState(false);
	const { isUnlocked, encrypt } = useSecureUnlock();
	const invalidate = useInvalidateAccounting();
	const [error, setError] = useState<string | null>(null);
	const [status, setStatus] = useState<BundleStatus | null>(null);
	const [batchId, setBatchId] = useState<string | null>(monitorBatchId ?? null);
	const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const mounted = useRef(true);
	const startedAtRef = useRef<number | null>(null);
	const rateSnapshotRef = useRef<{ journalCompleted: number; atMs: number } | null>(null);
	// When journal.pending first hits 0 the workflow is in its PROJECTION pass
	// (the long one) — record the moment so the ETA can count it down.
	const projectionStartRef = useRef<number | null>(null);
	const [, setElapsedTick] = useState(0);

	useEffect(() => () => {
		mounted.current = false;
		if (pollTimer.current) clearTimeout(pollTimer.current);
	}, []);

	// Tick elapsed time every second while the workflow is visible so ETAs refresh.
	useEffect(() => {
		if (phase !== 'starting' && phase !== 'running' && phase !== 'failed') return;
		const id = setInterval(() => setElapsedTick((t) => t + 1), 1000);
		return () => clearInterval(id);
	}, [phase]);

	const pick = useCallback(async () => {
		setError(null);
		const res = await DocumentPicker.getDocumentAsync({
			// QuickBooks CSV reports (Journal / AR / AP) plus the IIF lists export
			// as an OPTIONAL balance reference: its ACCNT rows carry QB's
			// as-of-export balances, which the preflight reconciles against the
			// journal. The IIF itself is never uploaded here — lists are imported
			// separately (Step 1). Listing extensions keeps macOS from greying out
			// files with odd/missing MIME types.
			type: ['.csv', '.iif', 'text/csv'],
			copyToCacheDirectory: true,
			multiple: true,
		});
		if (res.canceled || !res.assets?.length) return;
		const picked: PickedFile[] = [];
		for (const asset of res.assets) {
			let text: string;
			if (Platform.OS === 'web' && asset.file) {
				// QB exports are CP1252 — decode that way so the middle-dot survives.
				const buf = await (asset.file as File).arrayBuffer();
				text = new TextDecoder('windows-1252').decode(buf);
			} else {
				text = await FileSystem.readAsStringAsync(asset.uri, { encoding: FileSystem.EncodingType.UTF8 });
			}
			const c = classifyQbFile(asset.name, text.slice(0, 8192));
			picked.push({ name: asset.name, text, type: c.type, reason: c.reason });
		}
		setFiles((prev) => [...prev, ...picked]);
	}, []);

	const setType = (idx: number, type: QbFileType) => {
		setFiles((prev) => prev.map((f, i) => (i === idx ? { ...f, type } : f)));
	};
	const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

	const poll = useCallback(async (id: string) => {
		try {
			const s = await apiService.fetchApi(
				`/accounting/qb-bundle/status?importBatchId=${encodeURIComponent(id)}&associationId=${encodeURIComponent(associationId)}`,
			) as BundleStatus;
			if (!mounted.current) return;
			setStatus(s);
			const completed = journalCompleted(s.journal);
			if (rateSnapshotRef.current == null && completed > 0 && startedAtRef.current != null) {
				rateSnapshotRef.current = {
					journalCompleted: completed,
					atMs: Date.now() - startedAtRef.current,
				};
			}
			if (projectionStartRef.current == null && sumJournalCounts(s.journal) > 0 && (s.journal?.pending ?? 0) === 0) {
				projectionStartRef.current = Date.now();
			}
			if (s.done) {
				// Refresh the dashboard/accounts/GL caches so the imported data
				// shows the moment the modal closes — without this the page
				// behind it stays stale until a manual browser refresh.
				try { await invalidate(); } catch { /* best-effort */ }
				setPhase('done');
				return;
			}
			// A terminal Workflow failure never sets done:true, so without this the
			// loop would poll forever in the "running" phase. Stop and surface it.
			if (s.workflowStatus === 'errored' || s.workflowStatus === 'terminated') {
				setError(s.workflowError
					? `The rebuild failed: ${s.workflowError}`
					: `The rebuild failed (workflow ${s.workflowStatus}). A journal file may be invalid — check the import batch for details.`);
				setPhase('failed');
				return;
			}
		} catch {
			// transient — keep polling
		}
		if (mounted.current) pollTimer.current = setTimeout(() => poll(id), 2500);
	}, [associationId, invalidate]);

	// Monitor mode: attach to an in-flight (or finished) batch and resume polling.
	useEffect(() => {
		if (!monitorBatchId) return;
		setBatchId(monitorBatchId);
		setPhase('running');
		startedAtRef.current = monitorCreatedAt ? new Date(monitorCreatedAt).getTime() : Date.now();
		rateSnapshotRef.current = null;
		projectionStartRef.current = null;
		poll(monitorBatchId);
	}, [monitorBatchId, monitorCreatedAt, poll]);

	// Pre-import reconciliation: parse + cross-check every selected file
	// in-memory (nothing uploads) and surface coverage gaps / AR / AP / balance
	// mismatches BEFORE the workflow tears down the previous import. See
	// utils/qb-preflight.ts — this is what catches a truncated year export.
	const review = useCallback(() => {
		const workflowFiles = files.filter((f) => WORKFLOW_TYPES.includes(f.type));
		const iifFiles = files.filter((f) => f.type === 'iif');
		if (workflowFiles.length === 0 && iifFiles.length === 0) {
			setError('Add your QuickBooks exports — the IIF lists file plus the Journal / Transactions / Vendor CSVs.');
			return;
		}
		setError(null);
		setOverrideBlocked(false);
		setAutoCorrect(false);
		try {
			setIifParsed(iifFiles.length > 0 ? parseIIF(iifFiles.map((f) => f.text).join('\n')) : null);
			setPreflight(buildQbPreflightReport(files));
			setPhase('review');
		} catch (e) {
			setError(`Preflight check failed: ${(e as Error).message}`);
		}
	}, [files]);

	/**
	 * Apply the IIF lists (chart, customers, vendors) BEFORE the transactions
	 * import — the one-step flow. Rows are tagged with the bundle's batch id so
	 * a revert sweeps the structure too. Returns true when ok to continue.
	 */
	// When Reset is checked, wipe the WHOLE association's accounting data BEFORE
	// applying the new lists — backup-first. This runs ahead of applyLists on
	// purpose: re-applying customers over surviving prior accounts of the same
	// number would update the wrong twin, then the (qb_bundle-only) workflow
	// reset would soft-delete it. A full pre-wipe also clears non-bundle / manual
	// journal entries that block the init-only compiled load. No-op for fresh
	// associations (deletedTotal 0).
	const maybeResetAssociation = useCallback(async (): Promise<void> => {
		// forceResetRef lets "Retry with reset first" force a wipe without waiting
		// for the `reset` state toggle to flush (setReset + immediate re-run races).
		if (!reset && !forceResetRef.current) return;
		if (!association?.name) {
			throw new Error('Could not load the association name to confirm the reset — reopen and try again.');
		}
		setCompileStage('Backing up & resetting the association…');
		const res = await accountingService.resetAssociation({ associationId, confirmName: association.name });
		if (res.mode === 'sync') {
			if (res.backupKey) setResetBackupKey(res.backupKey);
			return;
		}
		// Workflow mode: the reset runs asynchronously and wipes EVERY accounting
		// row (incl. all import_batches) for the association. We MUST wait for it to
		// finish before creating the bundle batch / writing any rows, or the
		// still-running wipe deletes them. Poll the status beacon until done.
		let consecutiveErrors = 0;
		for (let i = 0; i < 300; i++) {
			await new Promise((r) => setTimeout(r, 2000));
			try {
				const status = await accountingService.getAssociationResetStatus(res.importBatchId);
				consecutiveErrors = 0;
				setCompileStage(`Resetting the association… (${status.currentStep ?? 'working'})`);
				if (status.done) {
					if (status.backupKey) setResetBackupKey(status.backupKey);
					return;
				}
			} catch {
				// A transient status-fetch hiccup shouldn't abort — tolerate a few.
				if (++consecutiveErrors >= 5) {
					throw new Error('Lost contact with the reset — it may still be running. Check the Batches list before retrying.');
				}
			}
		}
		throw new Error('Reset is still running — try the rebuild again once it finishes (check the Batches list).');
	}, [reset, association, associationId]);

	const applyLists = useCallback(async (batchId: string): Promise<boolean> => {
		if (!filteredIifParsed) return true;
		// Only require secure-unlock for tax IDs if vendors are actually being imported.
		const taxIds = includeVendors ? (preflight?.stats.structure?.vendorTaxIds ?? 0) : 0;
		const summary = await importIifLists({
			associationId,
			parsed: filteredIifParsed,
			encrypt: taxIds > 0 ? encrypt : null,
			importBatchId: batchId,
			onProgress: (p, now, total) => setCompileStage(
				p === 'glCodes' ? 'Importing GL codes…'
					: p === 'vendors' ? 'Importing vendors…'
						: `Importing customers (${now} of ${total})…`,
			),
		});
		setListsSummary(summary);
		return true;
	}, [filteredIifParsed, includeVendors, preflight, associationId, encrypt]);

	// The real compile → serialize → upload → start, shared by the no-unknowns
	// fast path and the post-prompt resume. Owns its own error surfacing so both
	// entry points report partial-save the same way.
	const runCompileAndStart = useCallback(async (ctx: {
		batchId: string;
		entries: ParsedEntry[];
		details: QbCompileDetails;
		resolutions: QbCompileClientResolutions;
	}): Promise<void> => {
		try {
			const { compiled } = await compileUploadAndStart({
				associationId,
				importBatchId: ctx.batchId,
				entries: ctx.entries,
				details: ctx.details,
				resolutions: ctx.resolutions,
				markCleared: true,
				reset,
				allowGaps: overrideBlocked || autoCorrect,
				onProgress: (stage, done, total) => {
					setCompileStage(stage === 'compiling' ? 'Compiling books locally…'
						: stage === 'uploading' ? `Uploading compiled data (part ${done + 1} of ${total})…`
							: 'Starting the import…');
				},
			});
			setCompiledAuditCount(compiled.audit.length);
			setCreatedGlCodes((compiled.rows.gl_codes ?? []).map((g) => {
				const r = g as Record<string, unknown>;
				return { code: String(r.code ?? ''), name: String(r.name ?? '') };
			}));
			setCompileStage(null);
			setPhase('running');
			poll(ctx.batchId);
		} catch (e) {
			setCompileStage(null);
			setError(withPartialSaveHint((e as Error).message, ctx.batchId));
			setPhase('error');
		}
	}, [associationId, reset, overrideBlocked, autoCorrect, poll]);

	// Show the GL-code mapping preview before compiling, so the user can review
	// (and override) how every journal GL code lands on the standard chart. With
	// no codes to map, skip straight to the compile.
	const presentGlMapping = useCallback((ctx: {
		batchId: string;
		entries: ParsedEntry[];
		details: QbCompileDetails;
		resolutions: QbCompileClientResolutions;
		standardDefaults: StandardGl[];
	}): void => {
		const mappings = probeGlCodeMappings(ctx.entries, ctx.resolutions, ctx.standardDefaults);
		if (mappings.length === 0) {
			void runCompileAndStart(ctx);
			return;
		}
		pendingCompileRef.current = ctx;
		setGlMappings(mappings);
		setCompileStage(null);
	}, [runCompileAndStart]);

	// Owner gate: before the GL-mapping step, probe for A/R names that don't
	// resolve to an account. Any hit BLOCKS with a review the user must
	// acknowledge (or cancel + fix). No unmatched owner is ever dropped silently.
	const gateOwnersThenGl = useCallback((ctx: {
		batchId: string;
		entries: ParsedEntry[];
		details: QbCompileDetails;
		resolutions: QbCompileClientResolutions;
		standardDefaults: StandardGl[];
	}): void => {
		const owners = probeUnresolvedOwners(associationId, ctx.batchId, ctx.entries, ctx.resolutions, ctx.details);
		if (owners.length > 0) {
			pendingCompileRef.current = ctx;
			setUnresolvedOwners(owners);
			setCompileStage(null);
			return; // resumes from onProceedOwners / onCancelOwners
		}
		presentGlMapping(ctx);
	}, [associationId, presentGlMapping]);

	// User resolved every unmatched owner (map onto an account, or skip →
	// journal-only): fold the maps into the resolver, then resume the compile.
	const onResolveOwners = useCallback((decisions: OwnerResolution[]): void => {
		const ctx = pendingCompileRef.current;
		setUnresolvedOwners(null);
		if (!ctx) return;
		applyOwnerResolutions(ctx.resolutions, decisions);
		presentGlMapping(ctx);
	}, [presentGlMapping]);

	const onCancelOwners = useCallback((): void => {
		const ctx = pendingCompileRef.current;
		setUnresolvedOwners(null);
		pendingCompileRef.current = null;
		setCompileStage(null);
		setError(withPartialSaveHint('Import canceled — fix the unmatched owner names/numbers and re-import.', ctx?.batchId ?? null));
		setPhase('error');
	}, []);

	// User confirmed the GL mapping: fold any overrides into the resolution map,
	// then resume the real compile.
	const onResolveGlCodes = useCallback((decisions: GlCodeResolution[]): void => {
		const ctx = pendingCompileRef.current;
		setGlMappings(null);
		if (!ctx) return;
		applyGlCodeResolutions(ctx.resolutions, decisions, ctx.standardDefaults);
		void runCompileAndStart(ctx);
	}, [runCompileAndStart]);

	const onCancelGlCodes = useCallback((): void => {
		const ctx = pendingCompileRef.current;
		setGlMappings(null);
		pendingCompileRef.current = null;
		setCompileStage(null);
		setError(withPartialSaveHint('Import canceled at the GL-code mapping step.', ctx?.batchId ?? null));
		setPhase('error');
	}, []);

	// User resolved every unknown payee: create/map the vendors, fold the ids
	// into the resolution map, then resume the (now clean) real compile.
	const onResolveVendors = useCallback(async (decisions: VendorResolution[]): Promise<void> => {
		const ctx = pendingCompileRef.current;
		setUnknownVendors(null);
		if (!ctx) return;
		try {
			setCompileStage('Creating vendors…');
			await applyVendorResolutions(ctx.resolutions, decisions);
		} catch (e) {
			setCompileStage(null);
			setError(withPartialSaveHint((e as Error).message, ctx.batchId));
			setPhase('error');
			return;
		}
		gateOwnersThenGl(ctx);
	}, [gateOwnersThenGl]);

	const onCancelVendors = useCallback((): void => {
		const ctx = pendingCompileRef.current;
		setUnknownVendors(null);
		pendingCompileRef.current = null;
		setCompileStage(null);
		setError(withPartialSaveHint('Import canceled — resolve the unrecognized payees to continue.', ctx?.batchId ?? null));
		setPhase('error');
	}, []);

	// v2 path: compile everything in the browser (entries + projected domain
	// rows + FIFO applications, with exact Trans#-keyed enrichment and the
	// optional auto-correction), upload the NDJSON parts, launch the
	// bulk-load workflow.
	const startCompiled = useCallback(async () => {
		// Tax-id gate FIRST: plaintext EINs in the IIF must be encrypted in the
		// browser before any upload (Secure Fields mandate).
		if ((preflight?.stats.structure?.vendorTaxIds ?? 0) > 0 && !isUnlocked) {
			setUnlockOpen(true);
			return;
		}
		setCompiledRun(true);
		setPhase('starting');
		setError(null);
		startedAtRef.current = Date.now();
		let createdBatchId: string | null = null;
		try {
			// 0) Full association reset (backup-first) when requested — BEFORE we
			//    create the bundle batch. The reset wipes EVERY import_batches row
			//    for the association, so a batch created first would be deleted out
			//    from under us and `/qb-bundle/start` would 404 ("Import batch").
			//    maybeReset waits for the reset workflow to finish before returning.
			await maybeResetAssociation();
			// 1) Now create the fresh bundle batch the rest of the import hangs off.
			const batch = await accountingService.createImportBatch({
				kind: ImportBatchKind.QbBundle,
				associationId,
				sourceFilename: `QuickBooks bundle (${files.length} files)`,
			});
			createdBatchId = batch.id;
			setBatchId(batch.id);

			// 2) IIF lists first — the structure the transactions resolve against.
			await applyLists(batch.id);

			// Structure-only bundle (just the IIF): done after the lists.
			const workflowFiles = files.filter((f) => WORKFLOW_TYPES.includes(f.type));
			if (workflowFiles.length === 0) {
				await invalidate();
				setCompileStage(null);
				setPhase('done');
				return;
			}

			setCompileStage('Fetching account / GL / vendor maps…');
			const resolutions = await buildQbCompileResolutions(associationId);
			if (autoCorrect && !resolutions.clearingGl) {
				throw new Error('Auto-correction needs a balance-sheet clearing/suspense GL (e.g. "Deposit Clearing Account") — none found in this association\'s chart.');
			}

			// Parse + dedupe the journal files (same parser the preflight ran).
			const entriesById = new Map<string, ParsedEntry>();
			for (const f of files.filter((x) => x.type === 'journal_csv')) {
				for (const e of parseJournalRows(parseCsv(f.text)).entries) {
					if (!entriesById.has(e.transactionId)) entriesById.set(e.transactionId, e);
				}
			}
			const toDetailRows = (kind: 'ar' | 'ap'): QbDetailRow[] => files
				.filter((x) => x.type === (kind === 'ar' ? 'transactions_csv' : 'vendor_transactions_csv'))
				.flatMap((f) => {
					const parsed = kind === 'ar' ? parseTransactionsCsv(f.text) : parseVendorCsv(f.text);
					return parsed.sections.flatMap((s) => s.transactions.map((t) => ({
						partyName: 'customerName' in s ? s.customerName : (s as { vendorName: string }).vendorName,
						type: t.type, transId: t.transId, dateMs: t.dateMs, num: t.num,
						memo: t.memo, category: 'category' in t ? (t as { category?: string }).category : undefined,
						amount: t.amount, account: t.account,
					})));
				});

			const entries = [...entriesById.values()];
			const details: QbCompileDetails = {
				ar: toDetailRows('ar'),
				ap: toDetailRows('ap'),
				autoCorrect: autoCorrect && resolutions.clearingGl ? { clearingGl: resolutions.clearingGl } : null,
			};

			// The global chart — the targets the GL-mapping preview maps onto.
			const stdResp = await accountingService.listGlCodes({ limit: 500, activeOnly: false });
			const standardDefaults: StandardGl[] = (stdResp.data ?? []).map((g) => ({
				id: g.id, code: g.code, name: g.name, accountType: g.accountType, externalId: g.externalId,
			}));

			// Probe compile (in-memory, discarded): surface any payee that isn't a
			// known vendor so the user can create-or-map it BEFORE the real run.
			setCompileStage('Checking vendors…');
			const unresolved = probeUnresolvedVendors(associationId, batch.id, entries, resolutions, details);
			const ctx = { batchId: batch.id, entries, details, resolutions, standardDefaults };
			if (unresolved.length > 0) {
				pendingCompileRef.current = ctx;
				setUnknownVendors(unresolved);
				setCompileStage(null);
				return; // resumes from onResolveVendors once the user decides
			}
			gateOwnersThenGl(ctx);
		} catch (e) {
			setCompileStage(null);
			setError(withPartialSaveHint((e as Error).message, createdBatchId));
			setPhase('error');
		}
	}, [files, associationId, autoCorrect, gateOwnersThenGl, preflight, isUnlocked, applyLists, maybeResetAssociation, invalidate]);

	const start = useCallback(async () => {
		const workflowFiles = files.filter((f) => WORKFLOW_TYPES.includes(f.type));
		const hasIif = files.some((f) => f.type === 'iif');
		if (workflowFiles.length === 0 && !hasIif) {
			setError('Add your QuickBooks exports — the IIF lists file plus the Journal / Transactions / Vendor CSVs.');
			return;
		}
		// /start rejects journal-less bundles — but only after the batch is
		// created and every file uploaded. Refuse up-front (even via the
		// preflight override) so we never strand a half-created batch.
		if (workflowFiles.length > 0 && !workflowFiles.some((f) => f.type === 'journal_csv')) {
			setError('A QuickBooks Journal CSV is required — the Transactions/Vendor reports only enrich the journal-derived ledger.');
			return;
		}
		if ((preflight?.stats.structure?.vendorTaxIds ?? 0) > 0 && !isUnlocked) {
			setUnlockOpen(true);
			return;
		}
		setCompiledRun(false);
		setPhase('starting');
		setError(null);
		startedAtRef.current = Date.now();
		rateSnapshotRef.current = null;
		projectionStartRef.current = null;
		let createdBatchId: string | null = null;
		try {
			// Full association reset (backup-first) when requested — BEFORE we create
			// the bundle batch. The reset wipes EVERY import_batches row for the
			// association, so a batch created first would be deleted out from under
			// us and `/qb-bundle/start` would 404 ("Import batch"). maybeReset waits
			// for the workflow to finish before returning.
			await maybeResetAssociation();
			const batch = await accountingService.createImportBatch({
				kind: ImportBatchKind.QbBundle,
				associationId,
				sourceFilename: `QuickBooks bundle (${files.length} files)`,
			});
			createdBatchId = batch.id;
			setBatchId(batch.id);

			// IIF lists first — one-step import (chart/customers/vendors, then
			// the CSV pipeline rebuilds the books against them).
			await applyLists(batch.id);
			if (workflowFiles.length === 0) {
				await invalidate();
				setCompileStage(null);
				setPhase('done');
				return;
			}

			const session = makeSessionId();
			const uploaded: Array<{ r2Key: string; filename: string; type: QbFileType }> = [];
			for (const f of workflowFiles) {
				const resp = await apiService.fetchApi(
					`/accounting/qb-bundle/upload?associationId=${encodeURIComponent(associationId)}&filename=${encodeURIComponent(f.name)}&session=${session}`,
					{ method: 'POST', headers: { 'Content-Type': 'text/csv' }, body: f.text, timeoutMs: 120_000 } as any,
				) as { r2Key: string };
				uploaded.push({ r2Key: resp.r2Key, filename: f.name, type: f.type });
			}

			await apiService.fetchApi('/accounting/qb-bundle/start', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({
					associationId,
					importBatchId: batch.id,
					files: uploaded,
					markCleared: true,
					reset,
					// True only when the user explicitly acknowledged blocking
					// preflight findings — lets the workflow's own coverage guard
					// stand down for this batch.
					allowGaps: overrideBlocked,
				}),
				timeoutMs: 60_000,
			} as any);

			setPhase('running');
			poll(batch.id);
		} catch (e) {
			setError(withPartialSaveHint((e as Error).message, createdBatchId));
			setPhase('error');
		}
	}, [files, associationId, reset, overrideBlocked, poll, preflight, isUnlocked, applyLists, maybeResetAssociation, invalidate]);

	// Relaunch the workflow after a terminal failure (errored/terminated). The
	// pipeline is idempotent server-side, so already-posted work is skipped and
	// the run resumes from whatever is still pending. The server replays the
	// original reset/markCleared intent persisted at /start; the backend also
	// CAS-guards workflow_id, so the ref here is just first-line double-click
	// protection.
	const retryInFlight = useRef(false);
	// Set by "Retry with reset first"; read by maybeResetAssociation so the forced
	// wipe happens even though `reset` state hasn't re-rendered yet.
	const forceResetRef = useRef(false);
	// Wipe the association (backup-first), then re-run the full compiled import
	// from scratch. Fixes CompiledLoadError "initialization-only: this association
	// already has N journal entries" — a plain retry re-drives the same batch and
	// can't clear the existing journal.
	const retryWithReset = useCallback(async () => {
		const ok = await new Promise<boolean>((resolve) => {
			Alert.alert(
				'Reset & rebuild from scratch?',
				`This deletes ALL accounting data for this association (a backup is taken first), then re-imports these files. ${RESET_ACCOUNTS_WARNING} This cannot be undone except by restoring the backup.`,
				[
					{ text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
					{ text: 'Reset & rebuild', style: 'destructive', onPress: () => resolve(true) },
				],
			);
		});
		if (!ok) return;
		forceResetRef.current = true;
		setReset(true);
		try {
			await startCompiled();
		} finally {
			forceResetRef.current = false;
		}
	}, [startCompiled]);
	const retry = useCallback(async () => {
		if (!batchId || retryInFlight.current) return;
		retryInFlight.current = true;
		setError(null);
		setPhase('running');
		startedAtRef.current = Date.now();
		rateSnapshotRef.current = null;
		projectionStartRef.current = null;
		try {
			await apiService.fetchApi('/accounting/qb-bundle/retry', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({ associationId, importBatchId: batchId }),
				timeoutMs: 60_000,
			} as any);
			poll(batchId);
		} catch (e) {
			setError((e as Error).message);
			setPhase('failed');
		} finally {
			retryInFlight.current = false;
		}
	}, [batchId, associationId, poll]);

	const hasWorkflowFile = files.some((f) => WORKFLOW_TYPES.includes(f.type));
	// One-step: an IIF alone is a valid (structure-only) bundle.
	const canReview = hasWorkflowFile || files.some((f) => f.type === 'iif');
	const correctableTotal = (preflight?.stats.correctable?.ar ?? 0) + (preflight?.stats.correctable?.ap ?? 0);
	const arTies = autoCorrect || !preflight?.findings.some((f) => f.code === 'ar_mismatch');
	const apTies = autoCorrect || !preflight?.findings.some((f) => f.code === 'ap_mismatch');
	// Auto-correction only resolves the CORRECTABLE_FINDINGS codes (it
	// synthesizes the missing detail rows). A blocking finding outside that set
	// — e.g. journal_missing, a parse error, an empty detail file — is NOT
	// fixed by auto-correct, so it must keep Start disabled even when other
	// blocks are correctable; otherwise a super admin could start a compiled
	// import made entirely of synthetic rows, bypassing the Journal-required
	// guard. The plain "import anyway" override (overrideBlocked) still covers
	// the user knowingly accepting an unmatched-books import.
	const blockingFindings = preflight?.findings.filter((f) => f.severity === 'blocking') ?? [];
	const uncorrectableBlock = blockingFindings.some((f) => !CORRECTABLE_FINDINGS.has(f.code));
	const autoCorrectClearsAllBlocks = blockingFindings.length > 0 && !uncorrectableBlock;
	// Start is allowed when: not blocked, OR the user ticked plain override, OR
	// auto-correct is on AND every remaining block is correctable.
	const startBlocked = preflight?.verdict === 'blocked'
		&& !overrideBlocked
		&& !(autoCorrect && autoCorrectClearsAllBlocks);
	const statusFileFlags = fileFlagsFromStatus(status?.files);
	// Compiled (v2) runs upload NDJSON parts instead of CSVs — progress is
	// parts-done over parts-total, not the parse/post/project step view.
	const compiledFiles = (status?.files ?? []).filter((f) => (f.overrideType ?? f.detectedType) === 'compiled_ndjson');
	const isCompiledRun = compiledRun || compiledFiles.length > 0 || (phase === 'starting' && compiledMode && isSuperAdmin && !isMonitor);
	const compiledPartsTotal = compiledFiles.length;
	const compiledPartsDone = compiledFiles.filter((f) => f.status === 'done').length;

	// One smooth bar across the whole one-step run. The 'starting' phase
	// (lists → compile → upload, all client-side) maps to the first ~45%,
	// keyed off the compileStage label set at each step; the workflow's
	// server-side part loading then carries 48→94%, and the closing balance
	// verify / recompute holds at ~96% until done. Labels are the source of
	// truth — keep this in sync with the setCompileStage(...) call sites.
	const compiledPercent = (() => {
		if (phase === 'failed') return Math.max(8, Math.round((compiledPartsDone / Math.max(1, compiledPartsTotal)) * 94));
		if (phase === 'starting') {
			const s = compileStage ?? '';
			if (/GL codes/.test(s)) return 10;
			if (/vendors/.test(s)) return 16;
			if (/customers/.test(s)) return 24;
			if (/account \/ GL/.test(s)) return 30;
			if (/Compiling/.test(s)) return 34;
			if (/Uploading/.test(s)) {
				const m = s.match(/part (\d+) of (\d+)/);
				return m ? 36 + Math.round((Number(m[1]) / Number(m[2])) * 10) : 40;
			}
			if (/Starting/.test(s)) return 47;
			return 4;
		}
		// running
		if (compiledPartsTotal > 0 && compiledPartsDone >= compiledPartsTotal) return 96;
		if (compiledPartsTotal > 0) return 48 + Math.round((compiledPartsDone / compiledPartsTotal) * 46);
		return 50;
	})();
	const compiledStageLabel = phase === 'starting'
		? (compileStage ?? 'Preparing…')
		: compiledPartsTotal > 0 && compiledPartsDone >= compiledPartsTotal
			? 'Verifying balances & recomputing…'
			: compiledPartsTotal > 0
				? `Loading data (${compiledPartsDone} of ${compiledPartsTotal} parts)`
				: 'Rebuilding…';
	const journalFileCount = isMonitor || status?.files?.length
		? statusFileFlags.journalFileCount
		: files.filter((f) => f.type === 'journal_csv').length;
	const hasArFile = isMonitor || status?.files?.length
		? statusFileFlags.hasArFile
		: files.some((f) => f.type === 'transactions_csv');
	const hasApFile = isMonitor || status?.files?.length
		? statusFileFlags.hasApFile
		: files.some((f) => f.type === 'vendor_transactions_csv');
	const elapsedMs = startedAtRef.current != null ? Date.now() - startedAtRef.current : 0;
	const blockDismiss = !isMonitor && (phase === 'running' || phase === 'starting' || phase === 'failed');
	// During 'starting' the browser is compiling + uploading the NDJSON parts to
	// R2 — closing now aborts in-flight uploads and corrupts the import. Lock the
	// window hard (no X, no backdrop dismiss). Once the workflow is launched
	// ('running') the work is server-side and the window is safe to close.
	const isUploading = !isMonitor && phase === 'starting';

	const progressProps = {
		phase: (phase === 'failed' ? 'running' : phase) as 'starting' | 'running' | 'done' | 'error',
		status,
		resetEnabled: reset,
		hasArFile,
		hasApFile,
		journalFileCount,
		elapsedMs,
		rateSnapshot: rateSnapshotRef.current,
	};

	return (
		<Modal visible onDismiss={blockDismiss ? () => {} : onClose} contentContainerStyle={styles.modal}>
			<View style={styles.header}>
				<Text style={styles.title}>{isMonitor ? 'QuickBooks rebuild progress' : 'Rebuild from QuickBooks'}</Text>
				{isUploading
					? <Ionicons name="hourglass-outline" size={20} color="#b26a00" />
					: <Pressable onPress={onClose} hitSlop={10}><Text style={styles.close}>✕</Text></Pressable>}
			</View>

			<ScrollView style={{ flex: 1 }} contentContainerStyle={styles.body}>
				{!isMonitor && phase === 'pick' && (
					<>
						<Text style={styles.intro}>
							Drop in ALL your QuickBooks exports at once: the IIF lists file (chart, customers, vendors) plus the year-split Journal CSVs (overlap is fine) and the Transactions (AR) / Vendor (AP) reports. One run imports the structure, rebuilds the ledger, and matches every detail row — and you get a preview before anything imports.
						</Text>

						<View style={styles.instructionsBox}>
							<Text style={styles.instructionsTitle}>How to export each file from QuickBooks Desktop</Text>
							<Text style={styles.instructionLine}>
								<Text style={styles.instructionStrong}>IIF lists</Text> — File → Utilities → Export → Lists to IIF Files… (chart of accounts, customers, vendors — tax IDs are encrypted in your browser).
							</Text>
							<Text style={styles.instructionLine}>
								<Text style={styles.instructionStrong}>Journal (one per year)</Text> — Reports → Accountant &amp; Taxes → Journal. Customize the date range, then Excel → &ldquo;a comma separated values (.csv) file&rdquo;.
							</Text>
							<Text style={styles.instructionLine}>
								<Text style={styles.instructionStrong}>Transactions (AR)</Text> — Reports → Customers &amp; Receivables → Customer Balance Detail → export to CSV.
							</Text>
							<Text style={styles.instructionLine}>
								<Text style={styles.instructionStrong}>Vendor Transactions (AP)</Text> — Reports → Vendors &amp; Payables → Vendor Balance Detail → export to CSV.
							</Text>
						</View>

						<Pressable style={styles.pickBtn} onPress={pick}>
							<Ionicons name="cloud-upload-outline" size={20} color="#1976d2" />
							<Text style={styles.pickBtnText}>Add QuickBooks files…</Text>
						</Pressable>

						{files.map((f, idx) => (
							<View key={`${f.name}-${idx}`} style={styles.fileRow}>
								<View style={styles.fileMeta}>
									<Text style={styles.fileName} numberOfLines={1}>{f.name}</Text>
									<Text style={styles.fileReason} numberOfLines={1}>{f.reason}</Text>
								</View>
								{f.type === 'iif' ? (
									<View style={[styles.chip, styles.chipIif]}>
										<Text style={styles.chipText}>{TYPE_LABEL.iif}</Text>
									</View>
								) : (
									<View style={styles.typeChips}>
										{TYPE_CHOICES.map((t) => (
											<Pressable
												key={t}
												style={[styles.chip, f.type === t && styles.chipActive]}
												onPress={() => setType(idx, t)}
											>
												<Text style={[styles.chipText, f.type === t && styles.chipTextActive]}>{TYPE_LABEL[t]}</Text>
											</Pressable>
										))}
									</View>
								)}
								<Pressable onPress={() => removeFile(idx)} hitSlop={8}><Ionicons name="trash-outline" size={18} color="#b71c1c" /></Pressable>
							</View>
						))}

						{files.length > 0 && <QbResetToggle checked={reset} onToggle={() => setReset((r) => !r)} />}

						{error && <Text style={styles.error}>{error}</Text>}

						<Pressable
							style={[styles.startBtn, !canReview && styles.startBtnDisabled]}
							onPress={review}
							disabled={!canReview}
							testID="qb-preflight-check"
						>
							<Text style={styles.startBtnText}>Check files</Text>
						</Pressable>
					</>
				)}

				{!isMonitor && phase === 'review' && preflight && (
					<View style={styles.progress} testID="qb-preflight-report">
						<Text style={styles.progressTitle}>
							{autoCorrect ? 'Ready — with auto-corrections applied'
								: preflight.verdict === 'blocked' ? 'These files won’t rebuild matching books'
									: preflight.verdict === 'warnings' ? 'Checks passed with warnings'
										: 'All checks passed'}
						</Text>
						<Text style={styles.progressNote}>
							{autoCorrect
								? `${correctableTotal.toLocaleString()} missing records will be synthesized as flagged correcting entries — AR and AP will tie to QuickBooks. Nothing has been imported yet.`
								: preflight.verdict === 'blocked'
									? 'Nothing has been imported. Each issue below says exactly what to export from QuickBooks — or apply auto-corrections to import now and tie out anyway.'
									: 'Nothing has been imported yet — this is a preview of what the dashboard will show.'}
						</Text>

						{/* Dashboard preview — what the association will look like after import */}
						<View style={styles.previewBox} testID="qb-preflight-stats">
							<Text style={styles.statsTitle}>DASHBOARD PREVIEW</Text>
							<View style={styles.tileRow}>
								<View style={styles.tile}>
									<Text style={styles.tileValue}>{(preflight.stats.structure?.customers ?? preflight.stats.ar?.parties ?? 0).toLocaleString()}</Text>
									<Text style={styles.tileLabel}>Customers</Text>
									{preflight.stats.ar && (
										<Text style={styles.tileSub}>{preflight.stats.ar.partiesWithBalance.toLocaleString()} with a balance due</Text>
									)}
								</View>
								<View style={styles.tile}>
									<Text style={styles.tileValue}>{(preflight.stats.structure?.vendors ?? preflight.stats.ap?.parties ?? 0).toLocaleString()}</Text>
									<Text style={styles.tileLabel}>Vendors</Text>
									{preflight.stats.ap && (
										<Text style={styles.tileSub}>{preflight.stats.ap.partiesWithBalance.toLocaleString()} with open bills</Text>
									)}
								</View>
								<View style={styles.tile}>
									<Text style={styles.tileValue}>{preflight.stats.journal.uniqueTransactions.toLocaleString()}</Text>
									<Text style={styles.tileLabel}>Transactions</Text>
									{preflight.stats.journal.firstMonth && preflight.stats.journal.lastMonth && (
										<Text style={styles.tileSub}>{monthRange(preflight.stats.journal.firstMonth)} – {monthRange(preflight.stats.journal.lastMonth)}</Text>
									)}
								</View>
							</View>
							<View style={styles.tileRow}>
								{preflight.stats.ar && (
									<View style={[styles.tile, arTies ? styles.tileOk : styles.tileBad]}>
										<Text style={styles.tileValue}>{usd(preflight.stats.ar.openBalanceCents)}</Text>
										<Text style={styles.tileLabel}>Accounts Receivable</Text>
										<Text style={arTies ? styles.tileOkText : styles.tileBadText}>
											{arTies ? '✓ ties to QuickBooks' : '✗ journal won’t match — auto-correct available'}
										</Text>
									</View>
								)}
								{preflight.stats.ap && (
									<View style={[styles.tile, apTies ? styles.tileOk : styles.tileBad]}>
										<Text style={styles.tileValue}>{usd(preflight.stats.ap.openBalanceCents)}</Text>
										<Text style={styles.tileLabel}>Accounts Payable</Text>
										<Text style={apTies ? styles.tileOkText : styles.tileBadText}>
											{apTies ? '✓ ties to QuickBooks' : '✗ journal won’t match — auto-correct available'}
										</Text>
									</View>
								)}
							</View>
							{!!preflight.stats.banks?.length && (
								<View style={styles.tileRow}>
									{preflight.stats.banks.map((b) => (
										<View key={b.code} style={[styles.tile, b.matches ? styles.tileOk : autoCorrect ? styles.tileWarn : styles.tileBad]}>
											<Text style={styles.tileValue}>{usd(b.journalCents)}</Text>
											<Text style={styles.tileLabel} numberOfLines={1}>{b.name}</Text>
											<Text style={b.matches ? styles.tileOkText : autoCorrect ? styles.tileWarnText : styles.tileBadText}>
												{b.matches ? '✓ matches QuickBooks'
													: autoCorrect ? `Δ ${usd(b.qbCents - b.journalCents)} parked in clearing`
														: `✗ QuickBooks says ${usd(b.qbCents)}`}
											</Text>
										</View>
									))}
								</View>
							)}
							{preflight.stats.structure && (
								<Text style={styles.statsLine}>
									<Text style={styles.statsStrong}>Structure:</Text> {preflight.stats.structure.glAccounts.toLocaleString()} GL accounts · {preflight.stats.structure.customers.toLocaleString()} customers · {preflight.stats.structure.vendors.toLocaleString()} vendors
									{preflight.stats.structure.vendorTaxIds > 0 ? ` · ${preflight.stats.structure.vendorTaxIds} tax IDs (encrypted in your browser)` : ''}
								</Text>
							)}
							{preflight.stats.structure && !isMonitor && (
								<View style={styles.listToggles} testID="qb-list-toggles">
									<Text style={styles.listTogglesLabel}>Include in import</Text>
									<View style={styles.listToggleRow}>
										<Pressable style={styles.listToggle} onPress={() => setIncludeChart((v) => !v)} testID="qb-toggle-chart">
											<Ionicons name={includeChart ? 'checkbox' : 'square-outline'} size={18} color={includeChart ? '#2e7d32' : '#9e9e9e'} />
											<Text style={[styles.listToggleText, !includeChart && styles.listToggleTextOff]}>
												Chart of accounts ({preflight.stats.structure.glAccounts.toLocaleString()})
											</Text>
										</Pressable>
										<Pressable style={styles.listToggle} onPress={() => setIncludeCustomers((v) => !v)} testID="qb-toggle-customers">
											<Ionicons name={includeCustomers ? 'checkbox' : 'square-outline'} size={18} color={includeCustomers ? '#2e7d32' : '#9e9e9e'} />
											<Text style={[styles.listToggleText, !includeCustomers && styles.listToggleTextOff]}>
												Customers ({preflight.stats.structure.customers.toLocaleString()})
												{listCounts.customers ? ` · ${listCounts.customers.add} new, ${listCounts.customers.edit} edit` : ''}
											</Text>
										</Pressable>
										<Pressable style={styles.listToggle} onPress={() => setIncludeVendors((v) => !v)} testID="qb-toggle-vendors">
											<Ionicons name={includeVendors ? 'checkbox' : 'square-outline'} size={18} color={includeVendors ? '#2e7d32' : '#9e9e9e'} />
											<Text style={[styles.listToggleText, !includeVendors && styles.listToggleTextOff]}>
												Vendors ({preflight.stats.structure.vendors.toLocaleString()})
												{listCounts.vendors ? ` · ${listCounts.vendors.add} new, ${listCounts.vendors.edit} edit` : ''}
											</Text>
										</Pressable>
									</View>
									{!includeChart && !includeCustomers && !includeVendors && (
										<Text style={styles.listToggleWarn}>Nothing selected — the lists step will be skipped.</Text>
									)}
								</View>
							)}
							{!preflight.stats.structure && (
								<Text style={styles.statsHint}>Add the IIF lists file too — it imports the chart, customers, and vendors in the same run and verifies bank balances.</Text>
							)}
						</View>

						{/* Auto-correction: a one-click fix that updates the preview */}
						{isSuperAdmin && compiledMode && correctableTotal > 0 && (
							autoCorrect ? (
								<View style={styles.correctApplied}>
									<Ionicons name="sparkles" size={18} color="#2e7d32" />
									<Text style={styles.correctAppliedText}>
										{correctableTotal.toLocaleString()} auto-corrections applied to the preview — every synthesized entry is flagged “[auto-correction]” in the journal.
									</Text>
									<Pressable onPress={() => setAutoCorrect(false)} hitSlop={8} testID="qb-auto-correct-undo">
										<Text style={styles.correctUndo}>Undo</Text>
									</Pressable>
								</View>
							) : (
								<Pressable style={styles.correctBtn} onPress={() => setAutoCorrect(true)} testID="qb-auto-correct">
									<Ionicons name="sparkles" size={18} color="#fff" />
									<Text style={styles.correctBtnText}>
										Apply auto-corrections ({correctableTotal.toLocaleString()} missing records)
									</Text>
								</Pressable>
							)
						)}

						{preflight.findings.map((f: QbPreflightFinding, i: number) => {
							const corrected = autoCorrect && f.severity === 'blocking' && CORRECTABLE_FINDINGS.has(f.code);
							return (
								<View key={`${f.code}-${i}`} style={[styles.findingRow, corrected && styles.findingCorrected]} testID={`qb-preflight-finding-${f.code}`}>
									<Ionicons
										name={corrected ? 'sparkles' : f.severity === 'blocking' ? 'close-circle' : f.severity === 'warning' ? 'warning' : 'checkmark-circle'}
										size={18}
										color={corrected ? '#2e7d32' : f.severity === 'blocking' ? '#b71c1c' : f.severity === 'warning' ? '#e65100' : '#2e7d32'}
									/>
									<View style={styles.findingMeta}>
										<Text style={styles.findingTitle}>{f.title}</Text>
										{corrected && (
											<Text style={styles.correctedNote}>Will be auto-corrected — synthesized entries cover the missing records; replace the export later for a perfect journal.</Text>
										)}
										{!!f.detail && !corrected && <Text style={styles.findingDetail}>{f.detail}</Text>}
										{!!f.fix && !corrected && (
											<View style={styles.fixBox}>
												<Text style={styles.fixLabel}>HOW TO FIX</Text>
												<Text style={styles.fixText}>{f.fix}</Text>
											</View>
										)}
									</View>
								</View>
							);
						})}

						{isSuperAdmin && (
							<Pressable style={styles.resetRow} onPress={() => setCompiledMode((v) => !v)} testID="qb-compiled-toggle">
								<Ionicons name={compiledMode ? 'checkbox' : 'square-outline'} size={20} color={compiledMode ? '#1976d2' : '#90a4ae'} />
								<Text style={styles.resetText}>
									Fast compiled import (recommended) — your browser builds the complete books locally and the server bulk-loads them. Exact Trans #-matched details, minutes instead of tens of minutes.
								</Text>
							</Pressable>
						)}

						{preflight.verdict === 'blocked' && !(compiledMode && autoCorrect && autoCorrectClearsAllBlocks) && (
							<Pressable
								style={styles.resetRow}
								onPress={() => setOverrideBlocked((v) => !v)}
								testID="qb-preflight-override"
							>
								<Ionicons name={overrideBlocked ? 'checkbox' : 'square-outline'} size={20} color={overrideBlocked ? '#b71c1c' : '#90a4ae'} />
								<Text style={styles.resetText}>
									{uncorrectableBlock
										? 'Import anyway — some issues above (e.g. a missing Journal file) can’t be auto-corrected; the books will NOT match QuickBooks.'
										: 'Import anyway — I understand the books will NOT match QuickBooks until corrected exports are re-imported.'}
								</Text>
							</Pressable>
						)}

						{error && <Text style={styles.error}>{error}</Text>}

						<Pressable
							style={[styles.startBtn, startBlocked && styles.startBtnDisabled]}
							onPress={compiledMode && isSuperAdmin ? startCompiled : start}
							disabled={startBlocked}
							testID="qb-preflight-start"
						>
							<Text style={styles.startBtnText}>{compiledMode && isSuperAdmin ? 'Compile & start rebuild' : 'Start rebuild'}</Text>
						</Pressable>
						<Pressable
							style={[styles.startBtn, styles.secondaryBtn]}
							onPress={() => { setPhase('pick'); setPreflight(null); setOverrideBlocked(false); }}
							testID="qb-preflight-back"
						>
							<Text style={[styles.startBtnText, styles.secondaryBtnText]}>
								{preflight.verdict === 'blocked' ? 'Back to files — replace the affected export' : 'Back to files'}
							</Text>
						</Pressable>
					</View>
				)}

				{(phase === 'starting' || phase === 'running' || phase === 'failed') && (
					<View style={styles.progress}>
						<Text style={styles.progressTitle}>
							{phase === 'failed' ? 'Rebuild stopped' : 'Rebuilding from QuickBooks'}
						</Text>
						{isUploading ? (
							<View style={styles.waitBanner}>
								<Ionicons name="warning" size={16} color="#b26a00" />
								<Text style={styles.waitText}>
									Uploading your books — keep this window open until it finishes. Closing now will interrupt the import.
								</Text>
							</View>
						) : (
							<Text style={styles.progressNote}>
								You can safely close this window — the import keeps running. Reopen it to check progress.
							</Text>
						)}
						{isCompiledRun ? (
							<View style={styles.barWrap} testID="qb-compiled-progress">
								<View style={styles.barTrack}>
									<View style={[styles.barFill, { width: `${phase === 'failed' ? compiledPercent : Math.max(2, compiledPercent)}%` }, phase === 'failed' && styles.barFillFailed]} />
								</View>
								<View style={styles.barLabelRow}>
									<Text style={styles.barStage}>{phase === 'failed' ? 'Stopped' : compiledStageLabel}</Text>
									<Text style={styles.barPct}>{compiledPercent}%</Text>
								</View>
								{phase === 'failed' && !!error && <Text style={styles.error}>{error}</Text>}
							</View>
						) : (
							<QbBundleProgressView
								{...progressProps}
								errorMessage={phase === 'failed' ? error : null}
							/>
						)}
						{batchId && status?.files?.some((f) => f.r2Key) && (
							<BundleFileDownloads associationId={associationId} importBatchId={batchId} files={status.files} />
						)}
					</View>
				)}

				{phase === 'done' && (
					<View style={styles.progress}>
						<Text style={styles.progressTitle}>
							{compiledPartsTotal === 0 && listsSummary ? 'Structure imported'
								: status?.hasErrors ? 'Rebuild finished with errors' : 'Rebuild complete'}
						</Text>
						{status?.hasErrors && (
							<Text style={styles.warn}>
								{status.filesErrored ? `${status.filesErrored} file(s) failed to parse and ` : ''}
								{status.journal?.error ? `${status.journal.error} journal entr${status.journal.error === 1 ? 'y' : 'ies'} errored — ` : ''}
								some postings are missing. Review the import batch before relying on these books.
							</Text>
						)}
						{resetBackupKey && (
							<View style={styles.statsBox}>
								<Text style={styles.statsTitle}>Pre-reset backup</Text>
								<Text style={styles.statsLine}>✓ Association backed up before the wipe.</Text>
								<Pressable onPress={() => accountingService.downloadAssociationResetBackup(resetBackupKey)}>
									<Text style={[styles.statsLine, { color: '#1565c0', textDecorationLine: 'underline' }]}>Download backup snapshot (JSON)</Text>
								</Pressable>
							</View>
						)}
						{listsSummary && (
							<View style={styles.statsBox}>
								<Text style={styles.statsTitle}>IIF lists</Text>
								<Text style={styles.statsLine}>
									✓ {listsSummary.glCodes.inserted} GL codes · {listsSummary.vendors.inserted + listsSummary.vendors.updated} vendors · {listsSummary.accounts} customer accounts{listsSummary.accountsUpdated > 0 ? ` (${listsSummary.accountsUpdated} updated)` : ''} ({listsSummary.people} owners)
									{listsSummary.errors.length > 0 ? ` · ${listsSummary.errors.length} row warnings` : ''}
								</Text>
							</View>
						)}
						{isCompiledRun && compiledPartsTotal > 0 ? (
							<View style={styles.statsBox}>
								<Text style={styles.statsTitle}>Compiled bulk load</Text>
								<Text style={styles.statsLine}>{compiledPartsDone} of {compiledPartsTotal} data parts loaded · balances verified ✓</Text>
							</View>
						) : (compiledPartsTotal === 0 && (listsSummary || isCompiledRun)) ? null : (
							<QbBundleProgressView {...progressProps} />
						)}
						{compiledAuditCount > 0 && (
							<Text style={styles.warn}>
								{compiledAuditCount.toLocaleString()} journal-only {compiledAuditCount === 1 ? 'entry' : 'entries'} (e.g. General Journal / payee-less checks) posted to the ledger with no customer/vendor line. The GL still balances — review or download them below.
							</Text>
						)}
						{createdGlCodes.length > 0 && (
							<View style={styles.createdGlBox}>
								<Text style={styles.createdGlTitle}>
									Created {createdGlCodes.length} new GL {createdGlCodes.length === 1 ? 'code' : 'codes'} (not on the standard chart):
								</Text>
								{createdGlCodes.slice(0, 50).map((g) => (
									<Text key={g.code} style={styles.createdGlRow}>{g.code} · {g.name}</Text>
								))}
								{createdGlCodes.length > 50 && (
									<Text style={styles.createdGlRow}>…and {createdGlCodes.length - 50} more</Text>
								)}
							</View>
						)}
						{batchId && <JournalOnlyEntries associationId={associationId} importBatchId={batchId} />}
						{batchId && !isCompiledRun && !listsSummary && (
							<Text style={styles.progressNote}>Unmatched report rows can be reviewed via the import batch ({batchId.slice(0, 8)}).</Text>
						)}
						{batchId && status?.files?.some((f) => f.r2Key) && (
							<BundleFileDownloads associationId={associationId} importBatchId={batchId} files={status.files} />
						)}
						<Pressable style={styles.startBtn} onPress={onClose}><Text style={styles.startBtnText}>Done</Text></Pressable>
					</View>
				)}

				{phase === 'failed' && (
					<View style={styles.failedActions}>
						<Pressable style={styles.startBtn} onPress={retry} testID="qb-bundle-retry">
							<Text style={styles.startBtnText}>Retry import</Text>
						</Pressable>
						{isSuperAdmin && isCompiledRun && (
							<Pressable style={[styles.startBtn, styles.resetRetryBtn]} onPress={retryWithReset} testID="qb-bundle-retry-reset">
								<Text style={styles.startBtnText}>Retry with reset first</Text>
							</Pressable>
						)}
						<Pressable style={[styles.startBtn, styles.secondaryBtn]} onPress={onClose}>
							<Text style={[styles.startBtnText, styles.secondaryBtnText]}>Close</Text>
						</Pressable>
					</View>
				)}

				{phase === 'error' && (
					<View style={styles.progress}>
						<Ionicons name="alert-circle" size={40} color="#b71c1c" />
						<Text style={styles.progressTitle}>Couldn’t start the rebuild</Text>
						<Text style={styles.error}>{error}</Text>
						<Pressable style={styles.startBtn} onPress={() => setPhase('pick')}><Text style={styles.startBtnText}>Back</Text></Pressable>
					</View>
				)}
			</ScrollView>

			<SecureUnlockModal
				visible={unlockOpen}
				onDismiss={() => setUnlockOpen(false)}
				onUnlocked={() => setUnlockOpen(false)}
				message="Enter the sensitive-data password to encrypt vendor tax IDs before upload."
			/>

			{unknownVendors && (
				<UnknownVendorModal
					visible
					unresolved={unknownVendors}
					vendors={pendingCompileRef.current?.resolutions.vendorList ?? []}
					onResolve={onResolveVendors}
					onCancel={onCancelVendors}
				/>
			)}

			{unresolvedOwners && (
				<UnresolvedOwnersModal
					visible
					unresolved={unresolvedOwners}
					accounts={pendingCompileRef.current?.resolutions.accountList ?? []}
					onResolve={onResolveOwners}
					onCancel={onCancelOwners}
				/>
			)}

			{glMappings && (
				<GlCodeMappingModal
					visible
					mappings={glMappings}
					standardDefaults={pendingCompileRef.current?.standardDefaults ?? []}
					onResolve={onResolveGlCodes}
					onCancel={onCancelGlCodes}
				/>
			)}
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { width: '92%', maxWidth: 640, height: '80%', borderRadius: 12, padding: 0 },
	header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#eee' },
	title: { fontSize: 18, fontWeight: '700' },
	close: { fontSize: 20, color: '#888', paddingHorizontal: 8 },
	body: { padding: 20, gap: 14 },
	intro: { color: '#546e7a', fontSize: 13, lineHeight: 19 },
	instructionsBox: { backgroundColor: '#f5f5f5', borderRadius: 10, padding: 14, gap: 8, borderWidth: 1, borderColor: '#eceff1' },
	instructionsTitle: { fontSize: 12, fontWeight: '700', color: '#37474f', marginBottom: 2 },
	instructionLine: { fontSize: 12, color: '#546e7a', lineHeight: 17 },
	instructionStrong: { fontWeight: '700', color: '#37474f' },
	pickBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 14, borderWidth: 1, borderStyle: 'dashed', borderColor: '#90caf9', borderRadius: 10, backgroundColor: '#e3f2fd' },
	pickBtnText: { color: '#1976d2', fontWeight: '700' },
	fileRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 8, backgroundColor: '#fafafa' },
	fileMeta: { flex: 1, minWidth: 0 },
	fileName: { fontSize: 13, fontWeight: '600', color: '#212121' },
	fileReason: { fontSize: 11, color: '#90a4ae' },
	typeChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, maxWidth: 260 },
	chip: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12, backgroundColor: '#eceff1' },
	chipActive: { backgroundColor: '#1976d2' },
	chipIif: { backgroundColor: '#f3e5f5' },
	findingRow: { flexDirection: 'row', gap: 8, padding: 10, borderWidth: 1, borderColor: '#eceff1', borderRadius: 8, backgroundColor: '#fafafa' },
	findingMeta: { flex: 1, minWidth: 0 },
	findingTitle: { fontSize: 13, fontWeight: '600', color: '#212121' },
	findingDetail: { fontSize: 12, color: '#546e7a', lineHeight: 17, marginTop: 2 },
	fixBox: { marginTop: 8, padding: 10, borderRadius: 8, backgroundColor: '#e8f0fe', borderWidth: 1, borderColor: '#c5d8f7' },
	fixLabel: { fontSize: 10, fontWeight: '700', color: '#1a56b0', letterSpacing: 0.6, marginBottom: 3 },
	fixText: { fontSize: 12, color: '#1c3e6e', lineHeight: 18 },
	barWrap: { alignSelf: 'stretch', gap: 8, paddingTop: 8 },
	barTrack: { height: 12, borderRadius: 6, backgroundColor: '#eceff1', overflow: 'hidden' },
	barFill: { height: 12, borderRadius: 6, backgroundColor: '#1976d2' },
	barFillFailed: { backgroundColor: '#b71c1c' },
	barLabelRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
	barStage: { fontSize: 13, color: '#546e7a', flex: 1, minWidth: 0 },
	barPct: { fontSize: 13, fontWeight: '700', color: '#1976d2' },
	statsBox: { padding: 12, borderRadius: 8, backgroundColor: '#f1f8e9', borderWidth: 1, borderColor: '#dcedc8', gap: 5 },
	statsTitle: { fontSize: 12, fontWeight: '700', color: '#33691e', letterSpacing: 0.4 },
	dlBox: { padding: 12, borderRadius: 8, backgroundColor: '#fafafa', borderWidth: 1, borderColor: '#eceff1', gap: 6, marginTop: 4 },
	disclosureHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 },
	joBox: { padding: 12, borderRadius: 8, backgroundColor: '#fffbeb', borderWidth: 1, borderColor: '#ffe082', gap: 8, marginTop: 4 },
	joHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
	joTitle: { fontSize: 12, fontWeight: '700', color: '#a06b00', letterSpacing: 0.4 },
	joList: { gap: 6 },
	joRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 5, borderTopWidth: 1, borderTopColor: '#ffe9b3' },
	joRowTop: { fontSize: 12, fontWeight: '600', color: '#5d4037' },
	joRowSub: { fontSize: 11, color: '#a1887f', marginTop: 1 },
	joAmount: { fontSize: 13, fontWeight: '700', color: '#5d4037', fontVariant: ['tabular-nums'] },
	joTruncated: { fontSize: 11, color: '#a1887f', fontStyle: 'italic', marginTop: 6, paddingTop: 6, borderTopWidth: 1, borderTopColor: '#ffe9b3' },
	dlRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
	dlName: { fontSize: 13, fontWeight: '600', color: '#212121' },
	dlMeta: { fontSize: 11, color: '#90a4ae', marginTop: 1 },
	dlBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, borderWidth: 1, borderColor: '#90caf9', backgroundColor: '#e3f2fd' },
	dlBtnText: { fontSize: 12, fontWeight: '700', color: '#1976d2' },
	statsLine: { fontSize: 12, color: '#37474f', lineHeight: 18 },
	statsStrong: { fontWeight: '700', color: '#212121' },
	statsHint: { fontSize: 11, color: '#90a4ae', fontStyle: 'italic' },
	listToggles: { marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#eceff1' },
	listTogglesLabel: { fontSize: 11, fontWeight: '700', color: '#607d8b', textTransform: 'uppercase', marginBottom: 6 },
	listToggleRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
	listToggle: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 2 },
	listToggleText: { fontSize: 13, color: '#37474f' },
	listToggleTextOff: { color: '#b0bec5', textDecorationLine: 'line-through' },
	listToggleWarn: { fontSize: 12, color: '#c62828', marginTop: 6 },
	previewBox: { padding: 12, borderRadius: 10, backgroundColor: '#fafafa', borderWidth: 1, borderColor: '#eceff1', gap: 10 },
	tileRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
	tile: { flexGrow: 1, flexBasis: 140, minWidth: 0, padding: 10, borderRadius: 8, backgroundColor: '#fff', borderWidth: 1, borderColor: '#e0e0e0', gap: 2 },
	tileValue: { fontSize: 18, fontWeight: '700', color: '#212121' },
	tileLabel: { fontSize: 11, fontWeight: '600', color: '#78909c', textTransform: 'uppercase', letterSpacing: 0.4 },
	tileSub: { fontSize: 11, color: '#90a4ae' },
	tileOk: { borderColor: '#a5d6a7', backgroundColor: '#f1f8e9' },
	tileOkText: { fontSize: 11, color: '#2e7d32', fontWeight: '600' },
	tileBad: { borderColor: '#ef9a9a', backgroundColor: '#fff5f5' },
	tileBadText: { fontSize: 11, color: '#b71c1c', fontWeight: '600' },
	tileWarn: { borderColor: '#ffe082', backgroundColor: '#fffbeb' },
	tileWarnText: { fontSize: 11, color: '#a06b00', fontWeight: '600' },
	correctBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 13, borderRadius: 10, backgroundColor: '#2e7d32' },
	correctBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
	correctApplied: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderRadius: 10, backgroundColor: '#f1f8e9', borderWidth: 1, borderColor: '#a5d6a7' },
	correctAppliedText: { flex: 1, fontSize: 12, color: '#2e7d32', lineHeight: 17, fontWeight: '600' },
	correctUndo: { fontSize: 12, color: '#1976d2', fontWeight: '700' },
	findingCorrected: { backgroundColor: '#f1f8e9', borderColor: '#c8e6c9' },
	correctedNote: { fontSize: 12, color: '#2e7d32', lineHeight: 17, marginTop: 2, fontWeight: '600' },
	chipText: { fontSize: 11, color: '#546e7a' },
	chipTextActive: { color: '#fff', fontWeight: '700' },
	warn: { fontSize: 12, color: '#e65100', backgroundColor: '#fff3e0', padding: 10, borderRadius: 8, lineHeight: 17 },
	createdGlBox: { backgroundColor: '#f1f8e9', borderRadius: 8, padding: 10, gap: 2 },
	createdGlTitle: { fontSize: 12, fontWeight: '700', color: '#33691e', marginBottom: 4 },
	createdGlRow: { fontSize: 12, color: '#33691e', fontFamily: Platform.select({ web: 'ui-monospace, Menlo, monospace', default: 'Courier' }) },
	resetRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
	resetText: { flex: 1, fontSize: 12, color: '#546e7a' },
	error: { color: '#b71c1c', fontSize: 13 },
	startBtn: { backgroundColor: '#1976d2', padding: 14, borderRadius: 10, alignItems: 'center', marginTop: 4 },
	failedActions: { gap: 8 },
	secondaryBtn: { backgroundColor: 'transparent', borderWidth: 1, borderColor: '#1976d2' },
	resetRetryBtn: { backgroundColor: '#c62828' },
	secondaryBtnText: { color: '#1976d2' },
	startBtnDisabled: { backgroundColor: '#b0bec5' },
	startBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
	progress: { alignSelf: 'stretch', gap: 12, paddingVertical: 12 },
	progressTitle: { fontSize: 16, fontWeight: '700', color: '#212121', textAlign: 'center' },
	progressNote: { fontSize: 12, color: '#78909c', textAlign: 'center', lineHeight: 18 },
	waitBanner: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: '#fff8e1', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12 },
	waitText: { flex: 1, fontSize: 12, color: '#8d6e00', lineHeight: 18 },
});
